const MOCK_STATE_KEY = 'captureez:e2e-auth-user';

const DEFAULT_MOCK_USER = {
  uid: 'cypress-owner',
  email: 'test@captureez.com',
  displayName: 'Test Care Owner',
};

const DEFAULT_MOCK_CHILD = {
  id: 'cypress-child-1',
  name: 'Mia Johnson',
  age: 8,
  birthDate: '2017-03-15',
  description: 'Demo child for Cypress browser tests',
  status: 'active',
  users: {
    care_owner: DEFAULT_MOCK_USER.uid,
    care_partners: ['cypress-partner'],
    caregivers: [],
    therapists: [],
    members: [DEFAULT_MOCK_USER.uid, 'cypress-partner'],
  },
  medicalProfile: {
    foodAllergies: [],
    currentMedications: [],
  },
};

const DEFAULT_MOCK_DATA = {
  authUser: DEFAULT_MOCK_USER,
  child: DEFAULT_MOCK_CHILD,
  careTeamsByChildId: {
    [DEFAULT_MOCK_CHILD.id]: [
      {
        userId: DEFAULT_MOCK_USER.uid,
        role: 'care_owner',
        displayName: DEFAULT_MOCK_USER.displayName,
        email: DEFAULT_MOCK_USER.email,
        photoURL: null,
      },
      {
        userId: 'cypress-partner',
        role: 'care_partner',
        displayName: 'Test Care Partner',
        email: 'partner@captureez.com',
        photoURL: null,
      },
    ],
  },
  roleByChildId: {
    [DEFAULT_MOCK_CHILD.id]: 'care_owner',
  },
  timelineSummaryByChildId: {
    [DEFAULT_MOCK_CHILD.id]: {
      totalEntries: 2,
      todayCount: 2,
      weekCount: 5,
      activityStreak: 3,
      lastActivityTime: '10:30 AM',
    },
  },
  providersByChildId: {
    [DEFAULT_MOCK_CHILD.id]: [],
  },
  doctorVisitsByChildId: {
    [DEFAULT_MOCK_CHILD.id]: [],
  },
  allEntriesByChildId: {
    [DEFAULT_MOCK_CHILD.id]: [],
  },
};

const getWindow = () => (typeof window !== 'undefined' ? window : null);

export const isE2EMockEnabled = () => {
  const browserWindow = getWindow();
  return Boolean(
    browserWindow && (
      browserWindow.__captureezE2E_MOCK === true ||
      browserWindow.localStorage?.getItem(MOCK_STATE_KEY)
    )
  );
};

export const getE2EMockData = () => {
  const browserWindow = getWindow();
  const stored = browserWindow?.localStorage?.getItem(MOCK_STATE_KEY);

  if (stored) {
    try {
      return { ...DEFAULT_MOCK_DATA, authUser: JSON.parse(stored) };
    } catch (error) {
      return DEFAULT_MOCK_DATA;
    }
  }

  return browserWindow?.__captureezE2E_DATA || DEFAULT_MOCK_DATA;
};

export const getE2EMockAuthUser = () => getE2EMockData().authUser || DEFAULT_MOCK_USER;

export const setE2EMockAuthUser = (user) => {
  const browserWindow = getWindow();
  if (!browserWindow) return;

  const nextUser = user || DEFAULT_MOCK_USER;
  browserWindow.localStorage?.setItem(MOCK_STATE_KEY, JSON.stringify(nextUser));
  browserWindow.__captureezE2E_DATA = {
    ...(browserWindow.__captureezE2E_DATA || DEFAULT_MOCK_DATA),
    authUser: nextUser,
  };

  browserWindow.dispatchEvent(new CustomEvent('captureez:e2e-auth-changed', {
    detail: { user: nextUser },
  }));
};

export const clearE2EMockAuthUser = () => {
  const browserWindow = getWindow();
  if (!browserWindow) return;

  browserWindow.localStorage?.removeItem(MOCK_STATE_KEY);
  if (browserWindow.__captureezE2E_DATA) {
    browserWindow.__captureezE2E_DATA = {
      ...browserWindow.__captureezE2E_DATA,
      authUser: null,
    };
  }

  browserWindow.dispatchEvent(new CustomEvent('captureez:e2e-auth-changed', {
    detail: { user: null },
  }));
};

const authListeners = new Set();

const normalizeText = (value = '') => String(value || '').trim();

const getMockStore = () => {
  const browserWindow = getWindow();
  if (!browserWindow) {
    return DEFAULT_MOCK_DATA;
  }

  if (!browserWindow.__captureezE2E_DATA) {
    browserWindow.__captureezE2E_DATA = { ...DEFAULT_MOCK_DATA };
  }

  return browserWindow.__captureezE2E_DATA;
};

const setMockStore = (nextData = {}) => {
  const browserWindow = getWindow();
  if (!browserWindow) {
    return DEFAULT_MOCK_DATA;
  }

  browserWindow.__captureezE2E_DATA = nextData;
  return browserWindow.__captureezE2E_DATA;
};

const updateMockStore = (updater) => {
  const current = { ...getMockStore() };
  const next = typeof updater === 'function' ? (updater(current) || current) : current;
  return setMockStore(next);
};

const nextMockId = (prefix, bucketName) => {
  const store = { ...getMockStore() };
  const counters = store.__mockCounters || {};
  const nextCount = Number(counters[bucketName] || 0) + 1;
  store.__mockCounters = {
    ...counters,
    [bucketName]: nextCount,
  };
  setMockStore(store);
  return `${prefix}-${nextCount}`;
};

const normalizeProviderSource = (source = {}) => ({
  kind: ['manual', 'extracted', 'imported', 'migrated', 'system'].includes(source.kind) ? source.kind : 'manual',
  sourceId: source.sourceId || null,
  sourceField: source.sourceField || null,
  sourceLabel: source.sourceLabel || null,
  sourceText: source.sourceText || null,
  confidence: source.confidence || null,
});

const normalizeMockProvider = (provider = {}) => ({
  id: provider.id || '',
  childId: normalizeText(provider.childId),
  name: normalizeText(provider.name),
  role: ['doctor', 'specialist', 'therapist', 'nurse', 'clinic', 'other'].includes(provider.role) ? provider.role : 'other',
  specialty: normalizeText(provider.specialty),
  organizationName: normalizeText(provider.organizationName),
  phone: normalizeText(provider.phone),
  email: normalizeText(provider.email),
  address: normalizeText(provider.address),
  notes: normalizeText(provider.notes),
  isActive: provider.isActive !== false,
  source: normalizeProviderSource(provider.source || {}),
  createdAt: provider.createdAt || null,
  updatedAt: provider.updatedAt || null,
  archivedAt: provider.archivedAt || null,
});

const getMockProviderList = (childId) => {
  const store = getMockStore();
  return Array.isArray(store.providersByChildId?.[childId])
    ? store.providersByChildId[childId]
    : [];
};

const setMockProviderList = (childId, providers) => updateMockStore((store) => {
  store.providersByChildId = {
    ...(store.providersByChildId || {}),
    [childId]: providers,
  };
  return store;
});

const upsertMockProvider = (providerData = {}) => {
  const normalized = normalizeMockProvider(providerData);
  const childId = normalized.childId;
  const providerId = normalized.id || nextMockId('provider', 'providers');
  const provider = {
    ...normalized,
    id: providerId,
    createdAt: normalized.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  const providers = [...getMockProviderList(childId)];
  const index = providers.findIndex((item) => item.id === providerId);
  if (index >= 0) {
    providers[index] = { ...providers[index], ...provider };
  } else {
    providers.push(provider);
  }
  setMockProviderList(childId, providers);
  return provider;
};

const updateMockProvider = (providerId, updatedData = {}) => {
  const childIds = Object.keys(getMockStore().providersByChildId || {});
  for (const childId of childIds) {
    const providers = [...getMockProviderList(childId)];
    const index = providers.findIndex((item) => item.id === providerId);
    if (index >= 0) {
      providers[index] = {
        ...providers[index],
        ...normalizeMockProvider({ ...providers[index], ...updatedData, id: providerId, childId }),
        updatedAt: new Date().toISOString(),
      };
      setMockProviderList(childId, providers);
      return providers[index];
    }
  }
  return null;
};

const archiveMockProvider = (providerId) => {
  const childIds = Object.keys(getMockStore().providersByChildId || {});
  for (const childId of childIds) {
    const providers = [...getMockProviderList(childId)];
    const index = providers.findIndex((item) => item.id === providerId);
    if (index >= 0) {
      providers[index] = {
        ...providers[index],
        isActive: false,
        archivedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setMockProviderList(childId, providers);
      return providers[index];
    }
  }
  return null;
};

const normalizeMockVisit = (visit = {}) => ({
  id: visit.id || '',
  childId: normalizeText(visit.childId),
  visitDate: normalizeText(visit.visitDate),
  visitDateTime: visit.visitDateTime || null,
  doctorName: normalizeText(visit.doctorName),
  specialty: normalizeText(visit.specialty),
  organizationName: normalizeText(visit.organizationName),
  providerId: visit.providerId || null,
  reasonForVisit: normalizeText(visit.reasonForVisit),
  diagnosis: normalizeText(visit.diagnosis),
  treatmentRecommendations: normalizeText(visit.treatmentRecommendations),
  summaryNotes: normalizeText(visit.summaryNotes),
  followUpDate: normalizeText(visit.followUpDate),
  status: visit.status || 'active',
});

const getMockVisitList = (childId) => {
  const store = getMockStore();
  return Array.isArray(store.doctorVisitsByChildId?.[childId])
    ? store.doctorVisitsByChildId[childId]
    : [];
};

const setMockVisitList = (childId, visits) => updateMockStore((store) => {
  store.doctorVisitsByChildId = {
    ...(store.doctorVisitsByChildId || {}),
    [childId]: visits,
  };
  return store;
});

const buildMockDoctorVisitTimelineEntry = (visitId, visitData = {}) => {
  const timestamp = new Date(visitData.visitDateTime || visitData.visitAt || visitData.timestamp || visitData.visitDate || Date.now());
  const providerLabel = [normalizeText(visitData.doctorName || visitData.providerName), normalizeText(visitData.specialty || visitData.providerSpecialty)].filter(Boolean).join(' · ');
  return {
    doctorVisitId: visitId,
    collection: 'dailyLogs',
    timelineType: 'doctor_visit',
    entryType: 'doctor_visit',
    childId: normalizeText(visitData.childId),
    providerId: visitData.providerId || null,
    providerName: normalizeText(visitData.doctorName || visitData.providerName) || null,
    providerSpecialty: normalizeText(visitData.specialty || visitData.providerSpecialty) || null,
    doctorName: normalizeText(visitData.doctorName) || null,
    specialty: normalizeText(visitData.specialty) || null,
    providerSnapshot: {
      providerId: visitData.providerId || null,
      doctorName: normalizeText(visitData.doctorName || visitData.providerName) || null,
      specialty: normalizeText(visitData.specialty || visitData.providerSpecialty) || null,
      organizationName: normalizeText(visitData.organizationName || visitData.providerOrganizationName) || null,
    },
    visitDate: normalizeText(visitData.visitDate) || null,
    visitDateTime: visitData.visitDateTime || null,
    reasonForVisit: normalizeText(visitData.reasonForVisit) || null,
    diagnosis: normalizeText(visitData.diagnosis) || null,
    treatmentRecommendations: normalizeText(visitData.treatmentRecommendations) || null,
    summaryNotes: normalizeText(visitData.summaryNotes) || null,
    followUpDate: normalizeText(visitData.followUpDate) || null,
    status: visitData.status || 'active',
    title: 'Doctor visit',
    titlePrefix: 'Doctor visit',
    body: providerLabel || 'Doctor visit',
    content: providerLabel || 'Doctor visit',
    text: providerLabel || 'Doctor visit',
    source: {
      kind: 'doctor_visit',
      sourceId: visitId,
      providerId: visitData.providerId || null,
      providerName: normalizeText(visitData.doctorName || visitData.providerName) || null,
      providerSpecialty: normalizeText(visitData.specialty || visitData.providerSpecialty) || null,
    },
    timestamp,
    entryDate: timestamp.toDateString(),
    entryDayKey: timestamp.toISOString().slice(0, 10),
  };
};

const upsertMockVisit = (visitId, visitData = {}) => {
  const normalized = normalizeMockVisit({ ...visitData, id: visitId });
  const childId = normalized.childId;
  const visits = [...getMockVisitList(childId)];
  const nextVisit = {
    ...normalized,
    id: visitId,
    updatedAt: new Date().toISOString(),
    createdAt: normalized.createdAt || new Date().toISOString(),
  };
  const index = visits.findIndex((item) => item.id === visitId);
  if (index >= 0) {
    visits[index] = { ...visits[index], ...nextVisit };
  } else {
    visits.push(nextVisit);
  }
  setMockVisitList(childId, visits);

  const store = getMockStore();
  const entriesByChildId = { ...(store.allEntriesByChildId || {}) };
  const nextEntry = buildMockDoctorVisitTimelineEntry(visitId, nextVisit);
  const existingEntries = Array.isArray(entriesByChildId[childId]) ? [...entriesByChildId[childId]] : [];
  const entryIndex = existingEntries.findIndex((entry) => entry.doctorVisitId === visitId || entry.id === visitId);
  if (entryIndex >= 0) {
    existingEntries[entryIndex] = { ...existingEntries[entryIndex], ...nextEntry, id: visitId };
  } else {
    existingEntries.push({ ...nextEntry, id: visitId });
  }
  entriesByChildId[childId] = existingEntries;
  updateMockStore((current) => {
    current.allEntriesByChildId = entriesByChildId;
    return current;
  });

  return nextVisit;
};

const updateMockVisit = (visitId, updatedData = {}) => {
  const childIds = Object.keys(getMockStore().doctorVisitsByChildId || {});
  for (const childId of childIds) {
    const visits = [...getMockVisitList(childId)];
    const index = visits.findIndex((item) => item.id === visitId);
    if (index >= 0) {
      const nextVisit = {
        ...visits[index],
        ...normalizeMockVisit({ ...visits[index], ...updatedData, id: visitId, childId }),
        updatedAt: new Date().toISOString(),
      };
      visits[index] = nextVisit;
      setMockVisitList(childId, visits);

      const store = getMockStore();
      const entriesByChildId = { ...(store.allEntriesByChildId || {}) };
      const nextEntry = buildMockDoctorVisitTimelineEntry(visitId, nextVisit);
      const existingEntries = Array.isArray(entriesByChildId[childId]) ? [...entriesByChildId[childId]] : [];
      const entryIndex = existingEntries.findIndex((entry) => entry.doctorVisitId === visitId || entry.id === visitId);
      if (entryIndex >= 0) {
        existingEntries[entryIndex] = { ...existingEntries[entryIndex], ...nextEntry, id: visitId };
      } else {
        existingEntries.push({ ...nextEntry, id: visitId });
      }
      entriesByChildId[childId] = existingEntries;
      updateMockStore((current) => {
        current.allEntriesByChildId = entriesByChildId;
        return current;
      });
      return nextVisit;
    }
  }
  return null;
};

const linkMockVisitProvider = (visitId, providerId) => {
  const childIds = Object.keys(getMockStore().doctorVisitsByChildId || {});
  for (const childId of childIds) {
    const visits = [...getMockVisitList(childId)];
    const index = visits.findIndex((item) => item.id === visitId);
    if (index >= 0) {
      const nextVisit = {
        ...visits[index],
        providerId,
        updatedAt: new Date().toISOString(),
      };
      visits[index] = nextVisit;
      setMockVisitList(childId, visits);

      const store = getMockStore();
      const entriesByChildId = { ...(store.allEntriesByChildId || {}) };
      const existingEntries = Array.isArray(entriesByChildId[childId]) ? [...entriesByChildId[childId]] : [];
      const entryIndex = existingEntries.findIndex((entry) => entry.doctorVisitId === visitId || entry.id === visitId);
      const nextEntry = buildMockDoctorVisitTimelineEntry(visitId, nextVisit);
      if (entryIndex >= 0) {
        existingEntries[entryIndex] = { ...existingEntries[entryIndex], ...nextEntry, id: visitId };
      }
      entriesByChildId[childId] = existingEntries;
      updateMockStore((current) => {
        current.allEntriesByChildId = entriesByChildId;
        return current;
      });
      return nextVisit;
    }
  }
  return null;
};

const archiveMockVisit = (visitId) => {
  const childIds = Object.keys(getMockStore().doctorVisitsByChildId || {});
  for (const childId of childIds) {
    const visits = [...getMockVisitList(childId)];
    const index = visits.findIndex((item) => item.id === visitId);
    if (index >= 0) {
      const nextVisit = {
        ...visits[index],
        status: 'deleted',
        deletedAt: new Date().toISOString(),
        deletedBy: getMockStore().authUser?.uid || null,
        updatedAt: new Date().toISOString(),
      };
      visits[index] = nextVisit;
      setMockVisitList(childId, visits);

      const store = getMockStore();
      const entriesByChildId = { ...(store.allEntriesByChildId || {}) };
      const existingEntries = Array.isArray(entriesByChildId[childId]) ? [...entriesByChildId[childId]] : [];
      const entryIndex = existingEntries.findIndex((entry) => entry.doctorVisitId === visitId || entry.id === visitId);
      if (entryIndex >= 0) {
        existingEntries[entryIndex] = {
          ...existingEntries[entryIndex],
          status: 'deleted',
          deletedAt: new Date().toISOString(),
          deletedBy: getMockStore().authUser?.uid || null,
          updatedAt: new Date().toISOString(),
        };
      }
      entriesByChildId[childId] = existingEntries;
      updateMockStore((current) => {
        current.allEntriesByChildId = entriesByChildId;
        return current;
      });
      return nextVisit;
    }
  }
  return null;
};

export const buildProviderReviewE2EMockData = () => {
  const now = new Date();
  const visitDateTime = now.toISOString();
  const childId = DEFAULT_MOCK_CHILD.id;
  const providerSnapshotName = 'Dr. Nora Finch';
  const providerSnapshotSpecialty = 'Pediatrician';
  const providerSnapshotOrg = 'Bright Kids Clinic';
  const visitId = 'visit-review-1';
  const providerId = 'provider-existing-1';
  const entryDayKey = visitDateTime.slice(0, 10);

  return {
    ...DEFAULT_MOCK_DATA,
    providersByChildId: {
      [childId]: [
        {
          id: providerId,
          childId,
          name: 'Dr. Rowan Hale',
          role: 'doctor',
          specialty: 'Neurology',
          organizationName: 'Northside Clinic',
          phone: '555-222-3333',
          email: 'rowan@example.com',
          address: '42 Practice Ave',
          notes: 'Existing provider for link flow',
          isActive: true,
          source: { kind: 'manual', sourceId: null, sourceField: null, sourceLabel: null, sourceText: null, confidence: null },
          createdAt: now.toISOString(),
          updatedAt: now.toISOString(),
          archivedAt: null,
        },
      ],
    },
    doctorVisitsByChildId: {
      [childId]: [
        {
          id: visitId,
          childId,
          visitDate: entryDayKey,
          visitDateTime,
          doctorName: providerSnapshotName,
          specialty: providerSnapshotSpecialty,
          organizationName: providerSnapshotOrg,
          providerId: null,
          reasonForVisit: 'Annual check-up',
          diagnosis: 'Healthy',
          treatmentRecommendations: 'Keep doing what works',
          summaryNotes: 'All good',
          followUpDate: entryDayKey,
          status: 'active',
          createdAt: now.toISOString(),
          updatedAt: now.toISOString(),
        },
      ],
    },
    allEntriesByChildId: {
      [childId]: [
        {
          id: visitId,
          collection: 'dailyLogs',
          timelineType: 'doctor_visit',
          entryType: 'doctor_visit',
          doctorVisitId: visitId,
          childId,
          providerId: null,
          providerName: providerSnapshotName,
          providerSpecialty: providerSnapshotSpecialty,
          doctorName: providerSnapshotName,
          specialty: providerSnapshotSpecialty,
          providerSnapshot: {
            providerId: null,
            doctorName: providerSnapshotName,
            specialty: providerSnapshotSpecialty,
            organizationName: providerSnapshotOrg,
          },
          visitDate: entryDayKey,
          visitDateTime,
          reasonForVisit: 'Annual check-up',
          diagnosis: 'Healthy',
          treatmentRecommendations: 'Keep doing what works',
          summaryNotes: 'All good',
          followUpDate: entryDayKey,
          status: 'active',
          title: 'Doctor visit',
          titlePrefix: 'Doctor visit',
          body: `${providerSnapshotName} · ${providerSnapshotSpecialty}`,
          content: `${providerSnapshotName} · ${providerSnapshotSpecialty}`,
          text: `${providerSnapshotName} · ${providerSnapshotSpecialty}`,
          source: {
            kind: 'doctor_visit',
            sourceId: visitId,
            providerId: null,
            providerName: providerSnapshotName,
            providerSpecialty: providerSnapshotSpecialty,
          },
          timestamp: now,
          entryDate: now.toDateString(),
          entryDayKey,
        },
      ],
    },
    timelineSummaryByChildId: {
      [childId]: {
        totalEntries: 1,
        todayCount: 1,
        weekCount: 1,
        activityStreak: 1,
        lastActivityTime: '9:30 AM',
      },
    },
  };
};

export const getMockProvidersForChild = (childId) => getMockProviderList(childId);
export const getMockDoctorVisitsForChild = (childId) => getMockVisitList(childId);
export const addMockProviderRecord = (providerData) => upsertMockProvider(providerData);
export const updateMockProviderRecord = (providerId, updatedData) => updateMockProvider(providerId, updatedData);
export const archiveMockProviderRecord = (providerId) => archiveMockProvider(providerId);
export const addMockDoctorVisitRecord = (visitId, visitData) => upsertMockVisit(visitId, visitData);
export const updateMockDoctorVisitRecord = (visitId, updatedData) => updateMockVisit(visitId, updatedData);
export const linkMockDoctorVisitProvider = (visitId, providerId) => linkMockVisitProvider(visitId, providerId);
export const archiveMockDoctorVisitRecord = (visitId) => archiveMockVisit(visitId);

export const subscribeE2EAuth = (callback) => {
  if (typeof callback !== 'function') {
    return () => {};
  }

  authListeners.add(callback);
  callback(getE2EMockAuthUser());

  const handleChange = (event) => {
    callback(event?.detail?.user || null);
  };

  const browserWindow = getWindow();
  browserWindow?.addEventListener('captureez:e2e-auth-changed', handleChange);

  return () => {
    authListeners.delete(callback);
    browserWindow?.removeEventListener('captureez:e2e-auth-changed', handleChange);
  };
};

export const getE2EMockRoleContext = () => {
  const data = getE2EMockData();
  const child = data.child;

  return {
    userRoles: { [child.id]: data.roleByChildId[child.id] || 'care_owner' },
    userPermissions: { [child.id]: ['add_daily_logs', 'invite_care_partner', 'invite_caregiver', 'invite_therapist', 'remove_team_members', 'edit_child', 'manage_settings', 'manage_privacy', 'transfer_ownership'] },
    userDisplayInfo: { [child.id]: { uid: data.authUser.uid, displayName: data.authUser.displayName } },
    childrenWithAccess: [child],
    loading: false,
  };
};

export const getE2EMockDashboardState = () => {
  const data = getE2EMockData();
  const child = data.child;
  const role = data.roleByChildId[child.id] || 'care_owner';

  const noop = () => {};
  const children = [child];
  const allEntries = data.allEntriesByChildId || { [child.id]: [] };
  const timelineSummary = data.timelineSummaryByChildId;

  return {
    user: data.authUser,
    theme: null,
    loading: false,
    children,
    ownChildren: [child],
    familyChildren: [],
    professionalChildren: [],
    allEntries,
    recentEntries: allEntries,
    timelineSummary,
    incidents: { [child.id]: [] },
    expandedCards: {},
    expandedCategories: {},
    highlightedActions: {},
    showDailyCareModal: false,
    dailyCareAction: null,
    dailyCareChild: null,
    showSleepLogSheet: false,
    sleepLogChild: null,
    showFoodLogSheet: false,
    foodLogChild: null,
    showBathroomLogSheet: false,
    bathroomLogChild: null,
    showIncidentModal: false,
    incidentChild: null,
    showFollowUpModal: false,
    followUpIncident: null,
    showPatternSuggestionModal: false,
    patternSuggestions: [],
    suggestionsChildId: null,
    showDailyHabitsModal: false,
    dailyHabitsInitialCategoryId: null,
    dailyHabitsChild: null,
    showQuickEntry: false,
    selectedChild: child,
    entryType: 'micro',
    quickEntryStep: 0,
    currentChildId: child.id,
    USER_ROLES: {
      CARE_OWNER: 'care_owner',
      CARE_PARTNER: 'care_partner',
      CAREGIVER: 'caregiver',
      THERAPIST: 'therapist',
    },
    isReadOnlyForChild: () => false,
    getUserRoleForChild: (childId) => (childId === child.id ? role : null),
    setCurrentChildId: noop,
    setExpandedCategories: noop,
    handleAddChild: noop,
    handleInviteTeamMember: noop,
    isCardExpanded: () => false,
    toggleCard: noop,
    handleQuickDataEntry: noop,
    handleEditChild: noop,
    handleDeleteChild: noop,
    handleDailyReport: noop,
    handleMessages: noop,
    handleGroupActionClick: noop,
    getTypeConfig: (type) => ({ icon: '📝', color: '#0f172a', label: type }),
    formatTimeAgo: () => '1h ago',
    handleAddChildSuccess: noop,
    handleEditChildSuccess: noop,
    handleCloseDailyCareModal: noop,
    handleCloseSleepLogSheet: noop,
    handleCloseFoodLogSheet: noop,
    handleCloseBathroomLogSheet: noop,
    handleCloseIncidentModal: noop,
    handleCloseFollowUpModal: noop,
    handleClosePatternSuggestionModal: noop,
    handleCreateCustomCategories: noop,
    setShowDailyHabitsModal: noop,
    handleCloseDailyHabitsModal: noop,
    handleTrack: noop,
    handleOpenSleepLog: noop,
    handleOpenFoodLog: noop,
    handleOpenBathroomLog: noop,
    handleOpenMedicalLog: noop,
    handleShowCareReport: noop,
    handleCloseCareReportModal: noop,
    showCareReportModal: false,
    careReportChild: null,
    checkForPatterns: noop,
    handleQuickEntryComplete: noop,
    handleQuickEntrySkip: noop,
    refreshDashboard: async () => {},
  };
};
